import Nango from "@nangohq/frontend";
import type { SharePointConnectionConfig } from "./nango-integrations.constants";
import { hasProperty } from "./object";
import { formatSharePointConnectError } from "./sharepoint-connect-error";

type NangoConnectResult = {
  providerConfigKey: string;
  connectionId: string;
};

type NangoProvider = "google-drive" | "onedrive" | "dropbox" | string;

function createNangoClient(sessionToken: string): Nango {
  return new Nango({
    connectSessionToken: sessionToken,
  });
}

/**
 * OAuth popup via connect session + nango.auth (Google Drive and SharePoint).
 */
async function connectViaAuthPopup(
  nango: Nango,
  provider: NangoProvider,
  params?: Record<string, string>,
): Promise<NangoConnectResult> {
  const result = await nango.auth(provider, params ? { params } : undefined);
  return {
    providerConfigKey: result.providerConfigKey,
    connectionId: result.connectionId,
  };
}

/**
 * Headless API-key auth via connect session (e.g. Fireflies).
 * Avoids Nango Connect UI iframe issues; credentials go directly to Nango.
 */
async function connectViaApiKey(
  sessionToken: string,
  provider: NangoProvider,
  apiKey: string,
): Promise<NangoConnectResult> {
  const nango = createNangoClient(sessionToken);
  const result = await nango.auth(provider, {
    credentials: { apiKey },
  });
  return {
    providerConfigKey: result.providerConfigKey,
    connectionId: result.connectionId,
  };
}

/**
 * Initiate authorization for an external provider using a backend-issued connect
 * session. OAuth providers use the auth popup; API-key providers use headless auth.
 */
export async function connectNangoProvider(
  sessionToken: string,
  provider: NangoProvider,
  options?: {
    sharePointConnectionConfig?: SharePointConnectionConfig;
    apiKey?: string;
  },
): Promise<NangoConnectResult> {
  const sharePointConnectionConfig = options?.sharePointConnectionConfig;
  const apiKey = options?.apiKey?.trim();

  try {
    if (apiKey) {
      return connectViaApiKey(sessionToken, provider, apiKey);
    }

    const nango = createNangoClient(sessionToken);

    if (sharePointConnectionConfig) {
      return connectViaAuthPopup(nango, provider, {
        tenantId: sharePointConnectionConfig.tenantId,
        tenant: sharePointConnectionConfig.tenant,
      });
    }

    return connectViaAuthPopup(nango, provider);
  } catch (error) {
    const message = hasProperty(error, "message")
      ? `${error.message}`
      : `${error}`;
    throw new Error(
      sharePointConnectionConfig
        ? formatSharePointConnectError(message)
        : message,
    );
  }
}
